import { Injectable, OnDestroy, inject } from '@angular/core';
import videojs from 'video.js';
import { Subject, Subscription } from 'rxjs';
import { Actions, ofActionDispatched } from '@ngxs/store';
import { Logout } from '@bo/ng-auth';

export type VideoJsPlayer = ReturnType<typeof videojs>;

@Injectable({ providedIn: 'root' })
export class VideoService implements OnDestroy {
  private readonly actions$ = inject(Actions);

  private activePlayer?: VideoJsPlayer;
  private currentUrl?: string;
  private readonly subscriptions = new Subscription();

  public onPlay$ = new Subject<string>();

  constructor() {
    // Listen for logout action and stop video
    this.subscriptions.add(
      this.actions$.pipe(ofActionDispatched(Logout)).subscribe(() => {
        this.stopCurrent();
      }),
    );
  }

  startVideo(url: string, container: HTMLElement) {
    // If a 2nd thumbnail is clicked, kill the previous PiP/video.
    if (this.activePlayer) {
      if (this.currentUrl === url) {
        // If opening the same video while it's in PiP, pull it back into the modal
        if (document.pictureInPictureElement) {
          document.exitPictureInPicture().catch(() => {});
        }
        const playerEl = this.activePlayer.el();
        playerEl.classList.remove('d-none');
        container.appendChild(playerEl);
        return;
      } else {
        this.stopCurrent();
      }
    }

    this.currentUrl = url;

    // Create the raw video element untethered from Angular's component template
    const videoElement = document.createElement('video');
    videoElement.className = 'video-js vjs-big-play-centered vjs-theme-city';
    videoElement.setAttribute('playsinline', 'true');
    container.appendChild(videoElement);

    this.activePlayer = videojs(videoElement, {
      autoplay: true,
      controls: true,
      fluid: true,
      sources: [{ src: url, type: 'application/x-mpegURL' }],
    });

    // Listen for PiP closing via the browser's native 'X' button
    const nativeVideo = this.activePlayer.el().querySelector('video');
    if (nativeVideo) {
      nativeVideo.addEventListener('leavepictureinpicture', () => {
        // If the player wrapper is currently hidden on the body, destroy it completely
        if (this.activePlayer?.el()?.parentElement === document.body) {
          this.stopCurrent();
        }
      });
    }

    this.onPlay$.next(url);
  }

  handleModalClose() {
    if (!this.activePlayer) return;

    const nativeVideo = this.activePlayer.el().querySelector('video');

    // Don't close the modal when going in PiP mode.
    if (nativeVideo && document.pictureInPictureElement === nativeVideo) {
      // Move it to the global body and hide the wrapper.
      // This allows the video to continue playing even if the user navigates away
      const playerEl = this.activePlayer.el();
      playerEl.classList.add('d-none');
      document.body.appendChild(playerEl);
    } else {
      // Not in PiP. Kill it normally.
      this.stopCurrent();
    }
  }

  stopCurrent() {
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture().catch(() => {});
    }
    if (this.activePlayer) {
      this.activePlayer.dispose();
      this.activePlayer = undefined;
      this.currentUrl = undefined;
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.stopCurrent();
  }
}
